import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useLocation as useAppLocation } from '../../contexts/LocationContext';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import './AuthButton.css';
import ForgotPasswordModal from './ForgotPasswordModal';
import LoginModal from './LoginModal';
import PasswordChangeModal from './PasswordChangeModal';
import SignupModal from './SignupModal';
import UserProfile from './UserProfile';
import { PlatformContextDebugInfo } from '../platform/PlatformDebugPanel';

const AuthButton: React.FC = () => {
  const { user, loading, signOut } = useAuth();
  const { userProfile, hasRole, hasAnyRole } = useUserRole();
  const { selectedLocation } = useAppLocation();
  const navigate = useNavigate();
  const [showLogin, setShowLogin] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position and close when clicking outside
  useEffect(() => {
    const updateDropdownPosition = () => {
      if (menuRef.current && showUserMenu) {
        const rect = menuRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY + 8,
          right: window.innerWidth - rect.right + window.scrollX
        });
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (showUserMenu) {
        const target = event.target as Element;
        if (!target.closest('.auth-button-user-menu')) {
          setShowUserMenu(false);
        }
      }
    };

    if (showUserMenu) {
      updateDropdownPosition();
      window.addEventListener('resize', updateDropdownPosition);
      window.addEventListener('scroll', updateDropdownPosition, true);
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', updateDropdownPosition);
      window.removeEventListener('scroll', updateDropdownPosition, true);
    };
  }, [showUserMenu]);

  if (loading) {
    return (
      <div className="auth-button-loading">
        <div className="auth-button-spinner"></div>
      </div>
    );
  }

  const handleDevelopersLogClick = () => {
    navigate('/changelog?tab=developers-log');
    setShowUserMenu(false);
  };

  const handleUserManagementClick = () => {
    navigate('/user-management');
    setShowUserMenu(false);
  };

  const handleMembershipRequestsClick = () => {
    navigate('/admin/membership-requests');
    setShowUserMenu(false);
  };

  const handleDemoRequestsClick = () => {
    navigate('/admin/demo-requests');
    setShowUserMenu(false);
  };

  const handleCommunityClick = () => {
    navigate('/community');
    setShowUserMenu(false);
  };

  const handleChangeLogClick = () => {
    navigate('/changelog');
    setShowUserMenu(false);
  };

  const handleSignOut = async () => {
    const { error } = await signOut();
    if (error) {
      console.error('Error signing out:', error);
    }
    setShowUserMenu(false);
  };

  if (user) {
    const showPlatformDebug = hasAnyRole(['super_admin', 'developer']);

    return (
      <>
        <div className="auth-button-user-menu" ref={menuRef}>
          <div className="auth-button-user" onClick={() => setShowUserMenu(!showUserMenu)}>
            <div className="auth-button-avatar">
              {user.user_metadata?.first_name?.[0] || user.email?.[0] || 'U'}
            </div>
            <span className="auth-button-name">
              {user.user_metadata?.first_name || 'User'}
            </span>
            <svg className="auth-button-chevron" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </div>

          {showUserMenu && (
            <div 
              className="auth-button-dropdown"
              style={{
                top: `${dropdownPosition.top}px`,
                right: `${dropdownPosition.right}px`
              }}
            >
              <div className="auth-button-dropdown-header">
                <div className="auth-button-dropdown-name">
                  {user.user_metadata?.first_name || 'User'}
                </div>
                <div className="auth-button-dropdown-email">
                  {user.email}
                </div>
                {userProfile && (
                  <div className="auth-button-dropdown-role">
                    {userProfile.role.replace('_', ' ').toUpperCase()}
                  </div>
                )}
              </div>
              
              <div className="auth-button-dropdown-divider"></div>
              
              <div className="auth-button-dropdown-menu">
                <button
                  className="auth-button-dropdown-item"
                  onClick={() => {
                    setShowProfile(true);
                    setShowUserMenu(false);
                  }}
                >
                  <span className="auth-button-dropdown-icon">👤</span>
                  Profile Settings
                </button>
                
                <button
                  className="auth-button-dropdown-item"
                  onClick={() => {
                    setShowPasswordChange(true);
                    setShowUserMenu(false);
                  }}
                >
                  <span className="auth-button-dropdown-icon">🔒</span>
                  Change Password
                </button>
                
                <button
                  className="auth-button-dropdown-item"
                  onClick={handleCommunityClick}
                >
                  <span className="auth-button-dropdown-icon">💬</span>
                  Community
                </button>
                
                <button
                  className="auth-button-dropdown-item"
                  onClick={handleChangeLogClick}
                >
                  <span className="auth-button-dropdown-icon">📝</span>
                  Change Log
                </button>
                
                <button
                  className="auth-button-dropdown-item"
                  onClick={handleDevelopersLogClick}
                >
                  <span className="auth-button-dropdown-icon">📋</span>
                  Developers' Log
                </button>
                
                {hasAnyRole(['super_admin', 'local_govt_admin']) && (
                  <button
                    className="auth-button-dropdown-item"
                    onClick={() => {
                      navigate('/admin/outreach');
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="auth-button-dropdown-icon">✉️</span>
                    Outreach Center
                  </button>
                )}

                {hasAnyRole(['super_admin', 'local_govt_admin']) && (
                  <button
                    className="auth-button-dropdown-item"
                    onClick={() => {
                      const locationName = selectedLocation || user?.user_metadata?.specific_location || '';
                      const slug = locationName ? supabaseLocationService.getSlugForLocation(locationName) : null;
                      navigate(slug ? `/${slug}/admin-settings` : '/admin-settings');
                      setShowUserMenu(false);
                    }}
                  >
                    <span className="auth-button-dropdown-icon">🛠️</span>
                    Admin Settings
                  </button>
                )}

                {hasRole('super_admin') && (
                  <>
                    <button
                      className="auth-button-dropdown-item"
                      onClick={handleUserManagementClick}
                    >
                      <span className="auth-button-dropdown-icon">👥</span>
                      User Management
                    </button>
                    <button
                      className="auth-button-dropdown-item"
                      onClick={handleMembershipRequestsClick}
                    >
                      <span className="auth-button-dropdown-icon">📋</span>
                      Membership Requests
                    </button>
                    <button
                      className="auth-button-dropdown-item"
                      onClick={handleDemoRequestsClick}
                    >
                      <span className="auth-button-dropdown-icon">📅</span>
                      Demo Requests
                    </button>
                  </>
                )}

                {showPlatformDebug && <PlatformContextDebugInfo />}
                
                <div className="auth-button-dropdown-divider"></div>
                
                <button
                  className="auth-button-dropdown-item auth-button-signout"
                  onClick={handleSignOut}
                >
                  <span className="auth-button-dropdown-icon">🚪</span>
                  Sign out
                </button>
              </div>
            </div>
          )}
        </div>

        <UserProfile
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
        />
        
        <PasswordChangeModal
          isOpen={showPasswordChange}
          onClose={() => setShowPasswordChange(false)}
        />

      </>
    );
  }

  return (
    <>
      <div className="auth-button-group">
        <button
          className="auth-button auth-button-outline"
          onClick={() => setShowLogin(true)}
        >
          Sign In
        </button>
        <button
          className="auth-button auth-button-primary"
          onClick={() => setShowSignup(true)}
        >
          Sign Up
        </button>
      </div>

      <LoginModal
        isOpen={showLogin}
        onClose={() => setShowLogin(false)}
        onSwitchToSignup={() => {
          setShowLogin(false);
          setShowSignup(true);
        }}
        onSwitchToForgotPassword={() => {
          setShowLogin(false);
          setShowForgotPassword(true);
        }}
      />

      <SignupModal
        isOpen={showSignup}
        onClose={() => setShowSignup(false)}
        onSwitchToLogin={() => {
          setShowSignup(false);
          setShowLogin(true);
        }}
      />

      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onSwitchToLogin={() => {
          setShowForgotPassword(false);
          setShowLogin(true);
        }}
      />
    </>
  );
};

export default AuthButton;
